import UIComponent from "../ui/component";
import api from "../api";

export class TextEdit extends UIComponent {
    historyByTab = new Map();
    historyIndexByTab = new Map();
    currentEditableSpan = null;
    inlineHandlersBySpan = new Map();

    init() {
        this.isEditable = this.editable === "1";
        
        this.includeComponents({
            container: 'document@[data-tbx-name="checkbox_show"]',
        });

        this.resultTabs = this.container.resultTabs;
        this.toolbarWrapper =
            document.querySelector(".transcribe-toolbar-sticky") ||
            document.querySelector(".transcribe-toolbar");

        this.resultTabs?.onChange(this.handleTabChange.bind(this));
        this.resultTabs?.onDelete(this.handleTabDelete.bind(this));
        this.resultTabs?.on?.("aiContentUpdated", () =>
            this.toggleToolbarPendingState(this.resultTabs?.getActiveTabId())
        );
        this.resultTabs?.contentWrapper?.addEventListener("click", this.handleContentClick.bind(this));

        this.container.undoBtn?.on("click", this.handleUndoClick.bind(this));
        this.container.redoBtn?.on("click", this.handleRedoClick.bind(this));
        this.handleTabChange(this.resultTabs?.getActiveTabId());
    }

    handleUndoClick(e) {
        e.preventDefault();
        this.isEditable && this.undo();
    }

    handleRedoClick(e) {
        e.preventDefault();
        this.isEditable && this.redo();
    }

    handleTabChange(tabId) {
        if (!tabId) return;
        
        const isCopy = this.resultTabs?.isCopyTab(tabId);
        const isAi = this.resultTabs?.isAiTab?.(tabId) || false;
        const showEditControls = isCopy && this.isEditable && !isAi;

        this.toggleToolbarPendingState(tabId);
        this.toggleToolbarVisibility(isCopy);
        this.toggleEditControls(showEditControls);
        this.toggleAiTabUi(isAi);

        isCopy && this.isEditable && this.ensureHistory(tabId);
        this.updateNavButtons(tabId);
    }

    toggleToolbarVisibility(isCopy) {
        this.container.toolbar__edit?.[isCopy ? "hide" : "show"]();
        this.container.toolbar__options?.[isCopy ? "hide" : "show"]();
        
    }

    toggleEditControls(showEditControls) {
        this.container.toolbar__nav?.[showEditControls ? "show" : "hide"]();
        this.container.undoBtn?.[showEditControls ? "enable" : "disable"]();
        this.container.redoBtn?.[showEditControls ? "enable" : "disable"]();
    }

    toggleAiTabUi(isAi) {
        this.container.toolbar__separator?.[isAi ? "hide" : "show"]?.();
        this.container.toolbar__ai_templates?.[isAi ? "hide" : "show"]?.();
        this.container.toolbar__translate?.[isAi ? "hide" : "show"]?.();
    }

    toggleToolbarPendingState(tabId) {
        if (!this.toolbarWrapper || !this.resultTabs?.getCopyContainer) return;
        const isAi = this.resultTabs?.isAiTab?.(tabId) || false;
        if (!isAi) {
            this.toolbarWrapper.classList.remove("d-none");
            return;
        }

        const container = this.resultTabs.getCopyContainer(tabId);
        const preloader = container?.querySelector(".ai_preloader");
        const isPending =
            preloader && !preloader.classList.contains("d-none");

        if (isPending) {
            this.toolbarWrapper.classList.add("d-none");
        } else {
            this.toolbarWrapper.classList.remove("d-none");
        }
    }

    handleTabDelete(tabId) {
        this.historyByTab.delete(tabId);
        this.historyIndexByTab.delete(tabId);
    }

    handleContentClick(event) {
        if (!this.isEditable) return;
        
        const tabId = this.resultTabs?.getActiveTabId();
        if (!this.resultTabs?.isCopyTab(tabId)) return;
        
        const span = event.target.closest("span[data-start-time]");
        if (!span || span.classList.contains("editing")) return;
        
        event.preventDefault();
        this.startInlineEdit(span);
    }

    startInlineEdit(span) {
        if (!this.isEditable) return;
        this.currentEditableSpan?.blur?.();
        const prev = this.inlineHandlersBySpan.get(span);
        if (prev) {
            span.removeEventListener("keydown", prev.onKeyDown);
            span.removeEventListener("paste", prev.onPaste);
            span.removeEventListener("blur", prev.onBlur);
            this.inlineHandlersBySpan.delete(span);
        }

        const originalText = span.textContent;
        const prevContentEditable = span.getAttribute("contenteditable");
        span.setAttribute("contenteditable", "true");
        span.classList.add("editing");
    
        this.currentEditableSpan = span;

        span.focus();
        try {
            const sel = window.getSelection?.();
            const range = document.createRange();
            range.selectNodeContents(span);
            sel?.removeAllRanges?.();
            sel?.addRange?.(range);
        } catch (_) {}

        let finalized = false;
        let cancelled = false;
        let breakChanged = false;

        const getBrAfterSpan = () => {
            let next = span.nextSibling;
            while (
                next &&
                next.nodeType === Node.TEXT_NODE &&
                (next.nodeValue ?? "") === ""
            ) {
                next = next.nextSibling;
            }
            if (next && next.nodeType === Node.ELEMENT_NODE && next.nodeName === "BR") {
                return next;
            }
            return null;
        };

        const initialHadBreak = !!getBrAfterSpan();

        const insertBreakAfterSpan = () => {
            const parent = span.parentNode;
            if (!parent) return;
            const existingBr = getBrAfterSpan();

            if (existingBr) {
                existingBr.remove();
                breakChanged = (!!getBrAfterSpan()) !== initialHadBreak;
                return;
            }

            parent.insertBefore(document.createElement("br"), span.nextSibling);
            breakChanged = (!!getBrAfterSpan()) !== initialHadBreak;
        };

        const finishEdit = (save) => {
            if (finalized) return;
            if (!span) return;
            finalized = true;
            
            span.classList.remove("editing");
            const rawText = String(span.textContent ?? "");
            const newText = rawText.replace(/\r?\n/g, " ");
            const changed = newText !== originalText || breakChanged;

            if (prevContentEditable == null) {
                span.removeAttribute("contenteditable");
            } else {
                span.setAttribute("contenteditable", prevContentEditable);
            }

            if (!save) {
                const hasNow = !!getBrAfterSpan();
                if (hasNow !== initialHadBreak) {
                    if (initialHadBreak) {
                        span.parentNode?.insertBefore?.(
                            document.createElement("br"),
                            span.nextSibling
                        );
                    } else {
                        getBrAfterSpan()?.remove();
                    }
                }
            }

            if (save && this.isEditable && changed) {
                span.textContent = newText;
                let next = span.nextSibling;
                while (next && next.nodeType === Node.TEXT_NODE && (next.nodeValue ?? "") === "") {
                    next = next.nextSibling;
                }
                if (next && next.nodeType === Node.ELEMENT_NODE && next.nodeName === "BR") {
                } else {
                    const hasSpaceNode =
                        next &&
                        next.nodeType === Node.TEXT_NODE &&
                        typeof next.nodeValue === "string" &&
                        /^\s/.test(next.nodeValue);
                    const nextIsWordSpan =
                        next &&
                        next.nodeType === Node.ELEMENT_NODE &&
                        next instanceof HTMLElement &&
                        next.matches?.('span[data-start-time]');
                    if (!hasSpaceNode && nextIsWordSpan) {
                        span.parentNode?.insertBefore?.(document.createTextNode(" "), next);
                    }
                }
            } else {
                span.textContent = originalText;
            }
            this.currentEditableSpan = null;

            const cur = this.inlineHandlersBySpan.get(span);
            if (cur) {
                span.removeEventListener("keydown", cur.onKeyDown);
                span.removeEventListener("paste", cur.onPaste);
                span.removeEventListener("blur", cur.onBlur);
                this.inlineHandlersBySpan.delete(span);
            }
            
            save && this.isEditable && changed && (this.pushHistory(), this.saveCopyChanges());
        };

        const onBlur = () => {
            if (cancelled) return;
            finishEdit(true);
        };

        const onKeyDown = (e) => {
            if (e.key === "Enter") {
                if (e.shiftKey) {
                    e.preventDefault();
                    insertBreakAfterSpan();
                    return;
                } else {
                    e.preventDefault();
                    span.blur();
                    return;
                }
            }
            if (e.key === "Escape") {
                e.preventDefault();
                cancelled = true;
                span.textContent = originalText;
                finishEdit(false);
                this.container?.saveLoading?.hide?.();
                this.container?.saveSucces?.hide?.();
                return;
            }
        };

        const onPaste = (e) => {
            try {
                e.preventDefault();
                const text = e.clipboardData?.getData?.("text/plain") ?? "";
                document.execCommand?.("insertText", false, text);
            } catch (_) {}
        };

        // Store handlers so we can deterministically detach them later.
        this.inlineHandlersBySpan.set(span, { onKeyDown, onPaste, onBlur });

        span.addEventListener("blur", onBlur, { once: true });
        span.addEventListener("keydown", onKeyDown);
        span.addEventListener("paste", onPaste);
    }

    ensureHistory(tabId) {
        if (!this.isEditable || this.historyByTab.has(tabId)) return;
        this.historyByTab.set(tabId, [this.resultTabs?.getCopyHtml(tabId) || ""]);
        this.historyIndexByTab.set(tabId, 0);
    }

    pushHistory() {
        if (!this.isEditable) return;
        
        const tabId = this.resultTabs?.getActiveTabId();
        if (!this.resultTabs?.isCopyTab(tabId)) return;
        
        const html = this.resultTabs?.getCopyHtml(tabId);
        if (!html) return;
        
        const history = this.historyByTab.get(tabId);
        const index = this.historyIndexByTab.get(tabId);
        
        history.splice(index + 1);
        history.push(html);
        this.historyIndexByTab.set(tabId, history.length - 1);
        this.updateNavButtons(tabId);
    }


    undo() {
        if (!this.isEditable) return;
        
        const tabId = this.resultTabs?.getActiveTabId();
        const history = this.historyByTab.get(tabId);
        const index = this.historyIndexByTab.get(tabId);
        
        if (!history || index <= 0) return;
        
        this.historyIndexByTab.set(tabId, index - 1);
        this.resultTabs?.setCopyHtml(tabId, history[index - 1]);
        this.updateNavButtons(tabId);
        this.saveCopyChanges();
    }

    redo() {
        if (!this.isEditable) return;
        
        const tabId = this.resultTabs?.getActiveTabId();
        const history = this.historyByTab.get(tabId);
        const index = this.historyIndexByTab.get(tabId);
        
        if (!history || index >= history.length - 1) return;
        
        this.historyIndexByTab.set(tabId, index + 1);
        this.resultTabs?.setCopyHtml(tabId, history[index + 1]);
        this.updateNavButtons(tabId);
        this.saveCopyChanges();
    }

    updateNavButtons(tabId) {
        const history = this.historyByTab.get(tabId) || [];
        const index = this.historyIndexByTab.get(tabId) ?? -1;
        const isCopy = this.resultTabs?.isCopyTab(tabId);
        
        const canUndo = this.isEditable && isCopy && index > 0;
        const canRedo = this.isEditable && isCopy && index < history.length - 1;

        this.container.undoBtn?.[!canUndo ? "addClass" : "removeClass"]("disabled-btn");
        this.container.redoBtn?.[!canRedo ? "addClass" : "removeClass"]("disabled-btn");
    }

    async saveCopyChanges() {
        if (!this.isEditable) return;
        const { saveLoading, saveSucces } = this.container;
        const tabEl = this.resultTabs?.getActiveTabElement();
        const tabId = tabEl?.getAttribute("data-id");
        const updateUrl = tabEl?.getAttribute("data-tab-update-url");

        if (!updateUrl || !this.resultTabs?.isCopyTab(tabId)) return;
        
        const content = this.resultTabs?.getCopyHtml(tabId);

        if (!content) return;
          
        saveLoading?.show();
        saveSucces?.hide();
        
        try {
            await api.post(updateUrl, { content });
            saveLoading?.hide();
            saveSucces?.show();
        } catch (error) {
            saveLoading?.hide();
        }
    }
}

